import Dinero from 'dinero.js';
import pluralize from 'pluralize';

import {
  type CurrentPlanPricing,
  type PlanPricing,
  type Subscription,
  type SubscriptionBillingCycle,
  type SubscriptionPlan,
} from '@air/types';
import { formatBytes } from '@air/utilities';

import { MONTHS_IN_YEAR } from '~/constants/unitsOfTime';
import { subscriptionPlanMap } from '~/utils/SubscriptionUtils';

type Plan = PlanPricing | CurrentPlanPricing;

const isCurrentPlan = (plan: Plan, subscription: Subscription) =>
  plan.id === subscription.plan || ('isCurrentPlan' in plan ? plan.isCurrentPlan : false);

// Active plans available to users for selection
const DEFAULT_VISIBLE_PLANS = ['starter', 'business', 'enterprise'];
const isDefaultVisiblePlan = (plan: Plan) => DEFAULT_VISIBLE_PLANS.includes(plan.id);

// Deprecated/legacy plans that should only appear when they match the current workspace subscription plan
const LEGACY_PLANS = ['plus', 'creator', 'pro'];
const isLegacyPlan = (plan: Plan) => LEGACY_PLANS.includes(plan.id);

const filterPlanList = (plans: Plan[], subscription: Subscription) =>
  plans.filter((plan) => {
    // Show free or deprecated/legacy plan if it matches the current workspace subscription plan
    if ((plan.id === 'basic' || isLegacyPlan(plan)) && isCurrentPlan(plan, subscription)) {
      return true;
    }

    // Otherwise, only show plan if it is currently active/visible (will exclude basic/free plan)
    return isDefaultVisiblePlan(plan);
  });

// Order in which plans should be displayed
const PLAN_SORT_ORDER = ['basic', 'creator', 'starter', 'plus', 'pro', 'business', 'enterprise'];
const sortPlanList = (plans: Plan[]) =>
  plans.sort((a, b) => PLAN_SORT_ORDER.indexOf(a.id) - PLAN_SORT_ORDER.indexOf(b.id));

export type UpgradePlanModalButtonAction =
  | 'current-plan'
  | 'self-service-downgrade'
  | 'book-demo'
  | 'self-service-upgrade-trial'
  | 'self-service-upgrade-trial-via-checkout'
  | 'self-service-upgrade'
  | 'self-service-upgrade-via-checkout';

// This is hard-coded to disable free trials while preserving respective code paths for future use
const ENABLE_FREE_TRIALS: boolean = false;

const getPlanButtonAction = ({
  plan,
  planIndex,
  subscribedPlanIndex,
}: {
  plan: Plan;
  planIndex: number;
  subscribedPlanIndex: number;
}): UpgradePlanModalButtonAction => {
  if (planIndex === subscribedPlanIndex) {
    return 'current-plan';
  }

  if (planIndex < subscribedPlanIndex) {
    return 'self-service-downgrade';
  }

  if (plan.id === 'enterprise') {
    return 'book-demo';
  }

  if (ENABLE_FREE_TRIALS) {
    return 'self-service-upgrade-trial-via-checkout';
  }

  return 'self-service-upgrade-via-checkout';
};

export const getPlanText = ({
  planId,
  pricingModel,
  limits,
  creditsPerCycle,
}: {
  planId: SubscriptionPlan;
  pricingModel: Plan['pricingModel'];
  limits: Plan['limits'];
  creditsPerCycle: Plan['creditsPerCycle'];
}):
  | {
      tagline?: string;
      credits?: string;
      options: string[];
      customPriceDescription?: string;
      isRecommended?: boolean;
      isLegacy?: boolean;
      secondaryCta?: 'book-demo';
    }
  | undefined => {
  const storageOptionItem = limits.storage ? `${formatBytes(limits.storage)} storage` : 'Unlimited storage';
  const membersOptionItem = limits.members ? `${pluralize('user', limits.members, true)}` : 'Unlimited users';

  const credits = creditsPerCycle
    ? `${new Intl.NumberFormat('en-US').format(creditsPerCycle)} credits every month`
    : undefined;

  switch (planId) {
    case 'basic': {
      if (pricingModel !== 'credit') {
        return {
          options: [membersOptionItem, storageOptionItem, '7 day deletion recovery'],
        };
      }

      return {
        credits,
        options: ['Up to 20 GB of storage or 24 AI images', '7 day deletion recovery'],
      };
    }

    case 'creator':
      return {
        options: [membersOptionItem, storageOptionItem, '30 day deletion recovery'],
        isLegacy: true,
      };

    case 'starter':
      return {
        tagline: 'More space and flexibility for scaling collaboration.',
        credits,
        options: ['Up to 100 GB storage or 120 AI images', 'Unlimited users', '30 day deletion recovery'],
      };

    case 'plus':
      return {
        options: [membersOptionItem, storageOptionItem, '30 day deletion recovery window'],
        isLegacy: true,
      };

    case 'pro':
      return {
        options: [membersOptionItem, storageOptionItem, '90 day deletion recovery'],
        isLegacy: true,
      };

    case 'business': {
      if (pricingModel !== 'credit') {
        return {
          tagline: 'Best for teams ready to level up with powerful AI tools.',
          options: [membersOptionItem, storageOptionItem, '90 day deletion recovery'],
          isLegacy: true,
        };
      }

      return {
        tagline: 'For teams looking to level-up with powerful AI tools.',
        credits,
        options: [
          'Up to 5 TB storage or 6,000 AI images',
          'Share link permissions',
          'Public API',
          '90 day deletion recovery',
        ],
        isRecommended: true,
        secondaryCta: 'book-demo',
      };
    }

    case 'enterprise':
      return {
        tagline: 'Tools for control, security, and support at scale.',
        customPriceDescription: 'Custom',
        credits: '60,000 credits every month',
        options: [
          'Up to 10 TB+ storage or 12,000+ AI images',
          'Unlimited users with custom roles & permissions',
          'SAML-based SSO',
          'Reporting, analytics, API Access & custom integrations',
          'Dedicated onboarding, support Slack, and CSM',
        ],
      };
  }
};

const formatPlanInfo = (options: {
  plan: Plan;
  subscription: Subscription;
  planIndex: number;
  subscribedPlanIndex: number;
}) => {
  const { plan, subscription } = options;

  const stripePlanIds: Partial<Record<SubscriptionBillingCycle, string>> = {};
  const prices: Partial<Record<SubscriptionBillingCycle, number>> = {};

  plan.billingCycles.forEach((billingCycle) => {
    stripePlanIds[billingCycle.id] = billingCycle.stripeId;
    prices[billingCycle.id] = billingCycle.planPrice;
  });

  const { pricingModel, creditsPerCycle, limits } = isCurrentPlan(plan, subscription)
    ? {
        pricingModel: subscription.pricingModel,
        creditsPerCycle: subscription.creditsPerCycle,
        limits: {
          storage: subscription.maxStorage,
          members: subscription.includedMembers,
        },
      }
    : plan;

  return {
    planId: plan.id,
    planTitle: subscriptionPlanMap[plan.id],
    action: getPlanButtonAction(options),
    stripePlanIds,
    prices,
    pricingModel,
    creditsPerCycle,
    isCurrentPlan: options.planIndex === options.subscribedPlanIndex,
    ...getPlanText({ planId: plan.id, pricingModel, limits, creditsPerCycle }),
  };
};

export type PlanInfoType = ReturnType<typeof formatPlanInfo> & {
  previousPlan: Omit<PlanInfoType, 'previousPlan'> | null;
};

export const getAvailablePlanInfo = ({
  plans,
  subscription,
}: {
  plans: Plan[];
  subscription: Subscription;
}): { currentPlan: PlanInfoType | null; availablePlans: PlanInfoType[] } => {
  const filteredPlans = filterPlanList(plans, subscription);
  const sortedPlans = sortPlanList(filteredPlans);
  const subscribedPlanIndex = sortedPlans.findIndex((plan) => isCurrentPlan(plan, subscription));

  const formattedPlans = sortedPlans.map((plan, index) =>
    formatPlanInfo({ plan, subscription, planIndex: index, subscribedPlanIndex }),
  );

  const formattedPlansWithPreviousPlan = formattedPlans.map((plan, index) => {
    let previousPlan = index > 0 ? formattedPlans[index - 1] : null;

    if (previousPlan?.planId === 'basic') {
      previousPlan = null;
    }

    return {
      ...plan,
      previousPlan,
    };
  });

  const { currentPlan, availablePlans } = formattedPlansWithPreviousPlan.reduce<{
    currentPlan: PlanInfoType | null;
    availablePlans: PlanInfoType[];
  }>(
    (acc, plan, index) => {
      if (index === subscribedPlanIndex) {
        acc.currentPlan = plan;
      }
      // Only show plans of a higher tier than the current plan
      else if (index > subscribedPlanIndex) {
        acc.availablePlans.push(plan);
      }

      return acc;
    },
    { currentPlan: null, availablePlans: [] },
  );

  return { currentPlan, availablePlans };
};

export const SUPPORTED_BILLING_CYCLES = ['annual', 'monthly'] satisfies SubscriptionBillingCycle[];

export type UpgradePlanModalBillingCycle = (typeof SUPPORTED_BILLING_CYCLES)[number];

export const isUpgradePlanModalBillingCycle = (value?: string | null): value is UpgradePlanModalBillingCycle => {
  return SUPPORTED_BILLING_CYCLES.some((cycle) => cycle === value);
};

export const formatMonthlyPrice = ({
  prices,
  billingCycle,
}: {
  prices: PlanInfoType['prices'];
  billingCycle: UpgradePlanModalBillingCycle;
}) => {
  if (!prices) {
    return null;
  }

  let amount = prices[billingCycle] ?? 0;

  if (billingCycle === 'annual') {
    amount = Math.ceil(amount / MONTHS_IN_YEAR);
  }

  return Dinero({ amount }).toFormat('$0,0');
};
